import { throttle } from 'lodash-es';
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import useResizeObserver from 'use-resize-observer';

import { clampPanelWidth, computeLayout } from './split-sizing-utils.js';
import { convertToPx, toCssLength, type PanelWidth } from './width-utils.js';

interface UseSplitSizingOptions {
  panel: {
    min: PanelWidth;
    max: PanelWidth;
    default: PanelWidth;
    position: 'start' | 'end';
    resizable: boolean;
    onResize?: (width: number) => void;
  };
  content: {
    min: PanelWidth;
  };
  refs: {
    root: RefObject<HTMLDivElement>;
    panel: RefObject<HTMLDivElement>;
    content: RefObject<HTMLDivElement>;
  };
}

interface SplitSizing {
  panel: {
    width: number;
    widthCss: string;
    minPx: number;
    maxPx: number;
  };
  content: {
    width: number;
    widthCss: string;
    minPx: number;
    maxPx: number;
  };
  handleWidth: number;
  dragActions: {
    onDragStart: () => void;
    onDrag: (mx: number) => void;
    onDragEnd: () => void;
  };
}

export function useSplitSizing(options: UseSplitSizingOptions): SplitSizing {
  const { panel, content, refs } = options;

  const [containerWidth, setContainerWidth] = useState(0);

  const [splitPx, setSplitPx] = useState<number>(() => {
    if (typeof panel.default === 'number') {
      return panel.default;
    }
    return -1;
  });

  const splitPxRef = useRef(splitPx);
  const isDraggingRef = useRef(false);
  const hasBeenManuallyResizedRef = useRef(false);
  const dragStartPx = useRef<number | null>(null);

  if (!isDraggingRef.current) {
    splitPxRef.current = splitPx;
  }

  const handleWidth = panel.resizable ? 8 : 0;
  const contentMinCss = toCssLength(content.min);

  const sizingConfig = useMemo(
    () => ({
      panelMin: panel.min,
      panelMax: panel.max,
      contentMin: content.min,
      handleWidth,
    }),
    [panel.min, panel.max, content.min, handleWidth],
  );

  const getClampedSplit = useCallback(
    (split: number, container: number) =>
      clampPanelWidth(split, container, sizingConfig),
    [sizingConfig],
  );

  useLayoutEffect(
    function initializeSplit() {
      if (containerWidth === 0) {
        return;
      }
      // For a numeric defaultWidth, initialize once and then let the user drive.
      if (typeof panel.default === 'number') {
        if (splitPx !== -1) {
          return;
        }
        setSplitPx(panel.default);
        return;
      }
      // For a percentage defaultWidth, re-derive from the container on every
      // container resize until the user has manually dragged. This ensures that
      // when the container itself is sized via %, the inner layout still applies
      // the percentage against the correct, fully-resolved container width.
      if (hasBeenManuallyResizedRef.current) {
        return;
      }
      setSplitPx(convertToPx(panel.default, containerWidth));
    },
    [containerWidth, panel.default, splitPx],
  );

  const KEEP_SQUISHED_SIZES = false;

  const throttledSync = useMemo(
    () =>
      throttle((width: number) => {
        const expected = getClampedSplit(splitPxRef.current, width);
        if (splitPxRef.current !== expected) {
          setSplitPx(expected);
        }
      }, 100),
    [getClampedSplit],
  );

  useResizeObserver({
    ref: refs.root,
    onResize: (size) => {
      if (!size.width) {
        return;
      }
      setContainerWidth(size.width);

      if (KEEP_SQUISHED_SIZES) {
        throttledSync(size.width);
      }
    },
  });

  const { position: panelPosition, onResize: onPanelResize } = panel;

  const dragActions = useMemo(
    () => ({
      onDragStart: () => {
        isDraggingRef.current = true;
        const containerW = refs.root.current?.offsetWidth ?? 0;
        const clamped = getClampedSplit(splitPxRef.current, containerW);
        splitPxRef.current = clamped;
        setSplitPx(clamped);
        dragStartPx.current = clamped;
      },
      onDrag: (mx: number) => {
        const containerW = refs.root.current?.offsetWidth ?? 0;
        if (dragStartPx.current === null || containerW === 0) {
          return;
        }

        const sign = panelPosition === 'start' ? 1 : -1;

        const next = dragStartPx.current + mx * sign;
        const clamped = getClampedSplit(next, containerW);
        splitPxRef.current = clamped;

        if (refs.panel.current) {
          refs.panel.current.style.width = `${clamped}px`;
        }
        if (refs.content.current) {
          refs.content.current.style.width = `max(${contentMinCss}, 100% - ${
            clamped + handleWidth
          }px)`;
        }
      },
      onDragEnd: () => {
        isDraggingRef.current = false;
        hasBeenManuallyResizedRef.current = true;
        dragStartPx.current = null;
        const rootWidth = refs.root.current?.offsetWidth ?? 0;
        const clamped = getClampedSplit(splitPxRef.current, rootWidth);
        splitPxRef.current = clamped;
        setSplitPx(clamped);
        onPanelResize?.(clamped);
      },
    }),
    [
      getClampedSplit,
      panelPosition,
      onPanelResize,
      handleWidth,
      contentMinCss,
      refs,
    ],
  );

  const layout = computeLayout(splitPx, containerWidth, sizingConfig);

  // When splitPx is -1 (percentage default, container not yet measured), use
  // the raw CSS percentage so the panel renders at the correct size on first
  // paint instead of collapsing to 0px and then jumping.
  // Content width is computed from the panel percentage so the two columns
  // add up to 100% without overflow.
  const isUnresolved = splitPx === -1;
  const panelWidthCss = isUnresolved
    ? toCssLength(panel.default)
    : `${layout.panel.width}px`;
  const contentWidthCss = isUnresolved
    ? `max(${toCssLength(content.min)}, 100% - ${toCssLength(
        panel.default,
      )} - ${handleWidth}px)`
    : layout.content.widthCss;

  return {
    ...layout,
    panel: { ...layout.panel, widthCss: panelWidthCss },
    content: { ...layout.content, widthCss: contentWidthCss },
    handleWidth,
    dragActions,
  };
}
